import React from "react";
import { X } from "lucide-react";
import RichTextEditor from "@/components/site/RichTextEditor";
import ImageUpload from "@/components/site/ImageUpload";

/**
 * Generic edit modal. Pass:
 *  - title: header text
 *  - fields: [{ key, label, type, options?, required?, multiline?, hint?, span? }]
 *  - initial: object with current values
 *  - onSave: async (values) => void
 *  - onClose: () => void
 */
export default function EditModal({ title, fields, initial, onSave, onClose }) {
  const [values, setValues] = React.useState(() => ({ ...initial }));
  const [saving, setSaving] = React.useState(false);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(values);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="edit-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="neo-card bg-white p-6 w-full max-w-3xl grid sm:grid-cols-2 gap-3 my-8" data-testid="edit-modal-form">
        <div className="sm:col-span-2 flex items-center justify-between">
          <h3 className="font-display font-black text-xl tracking-tighter uppercase">{title}</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 inline-flex items-center justify-center border-2 border-black rounded-md hover:bg-cef-yellow" data-testid="edit-modal-close"><X className="w-4 h-4" /></button>
        </div>
        {fields.map((f) => {
          const span = f.span === 2 ? "sm:col-span-2" : "";
          if (f.type === "richtext") {
            return (
              <div key={f.key} className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-tight">{f.label}{f.required && " *"}</label>
                <div className="mt-1">
                  <RichTextEditor value={values[f.key] ?? ""} onChange={(html) => set(f.key, html)} testId={`field-${f.key}`} />
                </div>
                {f.hint && <div className="text-xs text-neutral-500 mt-1">{f.hint}</div>}
              </div>
            );
          }
          if (f.type === "image") {
            return (
              <div key={f.key} className="sm:col-span-2">
                <ImageUpload value={values[f.key] ?? ""} onChange={(url) => set(f.key, url)} label={f.label + (f.required ? " *" : "")} testId={`field-${f.key}`} />
                {f.hint && <div className="text-xs text-neutral-500 mt-1">{f.hint}</div>}
              </div>
            );
          }
          if (f.type === "select") {
            return (
              <div key={f.key} className={span}>
                <label className="text-xs font-bold uppercase tracking-tight">{f.label}{f.required && " *"}</label>
                <select className="neo-input mt-1 w-full" value={values[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} required={f.required} data-testid={`field-${f.key}`}>
                  {!f.required && <option value="">—</option>}
                  {f.options.map((o) => typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            );
          }
          if (f.type === "checkbox") {
            return (
              <label key={f.key} className={`${span} flex items-center gap-2 text-sm font-bold mt-1`}>
                <input type="checkbox" checked={!!values[f.key]} onChange={(e) => set(f.key, e.target.checked)} data-testid={`field-${f.key}`} /> {f.label}
              </label>
            );
          }
          if (f.type === "textarea" || f.multiline) {
            return (
              <div key={f.key} className={span || "sm:col-span-2"}>
                <label className="text-xs font-bold uppercase tracking-tight">{f.label}{f.required && " *"}</label>
                <textarea className="neo-input mt-1 w-full min-h-[100px]" value={values[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} required={f.required} data-testid={`field-${f.key}`} />
                {f.hint && <div className="text-xs text-neutral-500 mt-1">{f.hint}</div>}
              </div>
            );
          }
          return (
            <div key={f.key} className={span}>
              <label className="text-xs font-bold uppercase tracking-tight">{f.label}{f.required && " *"}</label>
              <input
                className="neo-input mt-1 w-full"
                type={f.type || "text"}
                value={values[f.key] ?? ""}
                onChange={(e) => set(f.key, f.type === "number" ? (e.target.value ? Number(e.target.value) : "") : e.target.value)}
                required={f.required}
                data-testid={`field-${f.key}`}
              />
              {f.hint && <div className="text-xs text-neutral-500 mt-1">{f.hint}</div>}
            </div>
          );
        })}
        <div className="sm:col-span-2 flex gap-2 mt-2">
          <button type="submit" disabled={saving} className="neo-btn bg-cef-pink text-white flex-1 disabled:opacity-50" data-testid="edit-modal-save">{saving ? "Enregistrement…" : "Enregistrer"}</button>
          <button type="button" onClick={onClose} className="neo-btn bg-white" data-testid="edit-modal-cancel">Annuler</button>
        </div>
      </form>
    </div>
  );
}
